import {
  type AnyPromptPart,
  type HttpUserPrompt,
  isKnownPromptPart,
  type PagePromptContent as WirePageContent,
} from '@wandb/weave/lib/WbAgent/promptParts';
import type {
  NetworkAccessRequest,
  ToolCall,
  ToolCallResponseRecord,
  TurnResponse,
  TurnState,
} from '@wandb/weave/lib/WbAgent/WbAgentClient';
import {TERMINAL_STATES} from '@wandb/weave/lib/WbAgent/WbAgentClient';

import type {
  ContentPart,
  PagePromptContent,
  ReadonlyJSONValue,
  ToolCallPart,
} from '../store/thread';
import type {AssistantContentPart, RuntimeEvent} from './events';

export interface TurnEventExtractorOptions {
  /** skipUserMessages defines whether to drop user message events. This is
   * useful if, for example, the frontend adds them optimistically to its store.
   */
  skipUserMessages?: boolean;
  /**
   * Snapshot events normally replay as start/content/end so polling can share
   * the live reducer path. After partial SSE failure, repair mode lets the
   * canonical snapshot overwrite any partial visible parts instead.
   */
  snapshotEventMode?: 'append' | 'repair';
}

/**
 * TurnEventExtractor converts {@link TurnResponse} poll snapshots into
 * {@link RuntimeEvent}s, emitting only events new since the previous call.
 *
 * A turn maps to at most two message nodes: `${turnId}:user` and
 * `${turnId}:assistant`, where the assistant's message contains all parts of
 * its response (e.g. reasoning, text, and tool calls).
 */
export class TurnEventExtractor {
  private lastSeenMessageIndex = 0;

  /**
   * pendingToolCallResults holds call IDs whose `tool-call-result` hasn't been
   * emitted yet — the response record may arrive in a later poll than the
   * declaring message.
   */
  private pendingToolCallResults = new Set<string>();

  /**
   * userMessageEmitted tracks whether we've already seen a user-role message
   * for this turn. The backend creates one turn per user message, so this
   * should never flip more than once — but we guard against it because
   * `turn.user_prompt` is canonical and would be re-emitted otherwise.
   */
  private userMessageEmitted = false;

  constructor(
    private readonly turnId: string,
    private readonly opts?: TurnEventExtractorOptions
  ) {}

  /**
   * extract processes the latest turn snapshot and returns events for new data
   * since the last call.
   */
  extract(turn: TurnResponse): RuntimeEvent[] {
    const events: RuntimeEvent[] = [];

    // Only responses are needed — invocations come from `msg.tool_calls`.
    const responses = new Map<string, ToolCallResponseRecord>();
    for (const toolCall of turn.tool_calls ?? []) {
      if (toolCall.type === 'response') {
        responses.set(toolCall.call_id, toolCall);
      }
    }

    const messages = turn.messages ?? [];
    if (this.opts?.snapshotEventMode === 'repair') {
      return this.extractRepairEvents(turn, responses, messages);
    }

    for (let i = this.lastSeenMessageIndex; i < messages.length; i++) {
      const msg = messages[i];
      if (msg.role == null) {
        continue;
      }

      switch (msg.role) {
        case 'user':
          // The backend creates one turn per user message, so a turn should
          // only ever have a single user-role message. Defend against that
          // invariant changing — `turn.user_prompt` is canonical and would
          // otherwise be re-emitted for each user message in the turn.
          if (this.userMessageEmitted) {
            console.error(
              `[WBAgent] TurnEventExtractor: turn ${this.turnId} has multiple user messages; ignoring subsequent ones`
            );
            break;
          }
          this.userMessageEmitted = true;

          if (!this.opts?.skipUserMessages) {
            const userEvent = userMessageEventForTurn(turn);
            if (userEvent) {
              events.push(userEvent);
            }
          }
          break;

        case 'assistant':
          if (msg.content) {
            events.push(
              ...makeContentEvents(
                'text-message',
                this.assistantMessageId,
                msg.content
              )
            );
          }
          if (msg.tool_calls?.length) {
            for (const toolCall of msg.tool_calls) {
              events.push(
                ...makeToolCallEvents(toolCall, this.assistantMessageId)
              );
              this.pendingToolCallResults.add(toolCall.id);
            }
          }
          break;

        case 'reasoning':
          if (msg.content) {
            events.push(
              ...makeContentEvents(
                'reasoning',
                this.assistantMessageId,
                msg.content
              )
            );
          }
          break;

        case 'tool':
          // Results are emitted from `turn.tool_calls` instead — it carries
          // richer data (structured output + error flag) than this message.
          break;
      }
    }
    this.lastSeenMessageIndex = messages.length;

    // Emit results for any pending calls whose response record is now
    // available. `tool-call-result` mutates an existing part via toolCallId,
    // so late arrival doesn't affect `content[]` ordering.
    for (const callId of this.pendingToolCallResults) {
      const response = responses.get(callId);
      if (!response) {
        continue;
      }

      events.push(makeToolCallResultEvent(response));
      this.pendingToolCallResults.delete(callId);
    }

    return events;
  }

  private extractRepairEvents(
    turn: TurnResponse,
    responses: Map<string, ToolCallResponseRecord>,
    messages: NonNullable<TurnResponse['messages']>
  ): RuntimeEvent[] {
    const events: RuntimeEvent[] = [];

    for (const msg of messages) {
      if (msg.role !== 'user' || this.userMessageEmitted) {
        continue;
      }
      this.userMessageEmitted = true;
      if (!this.opts?.skipUserMessages) {
        const userEvent = userMessageEventForTurn(turn);
        if (userEvent) {
          events.push(userEvent);
        }
      }
    }

    const content = assistantContentForSnapshot(messages, responses);
    if (content.length > 0 || TERMINAL_STATES.includes(turn.state)) {
      events.push({
        type: 'assistant-content-replace',
        messageId: this.assistantMessageId,
        content,
      });
    }

    return events;
  }

  private get assistantMessageId(): string {
    return assistantMessageId(this.turnId);
  }
}

function assistantContentForSnapshot(
  messages: NonNullable<TurnResponse['messages']>,
  responses: Map<string, ToolCallResponseRecord>
): AssistantContentPart[] {
  const content: AssistantContentPart[] = [];

  for (const message of messages) {
    if (message.role === 'reasoning' && message.content) {
      content.push({
        type: 'thinking',
        text: message.content,
        status: 'complete',
      });
      continue;
    }
    if (message.role !== 'assistant') {
      continue;
    }
    if (message.content) {
      content.push({
        type: 'text',
        text: message.content,
        status: 'complete',
      });
    }
    for (const toolCall of message.tool_calls ?? []) {
      content.push(
        toolCallPartForSnapshot(toolCall, responses.get(toolCall.id))
      );
    }
  }

  return content;
}

function toolCallPartForSnapshot(
  toolCall: ToolCall,
  response: ToolCallResponseRecord | undefined
): ToolCallPart {
  const argsText = toolCall.function.arguments;
  const base: Omit<ToolCallPart, 'status' | 'result' | 'isError'> = {
    type: 'tool-call',
    toolCallId: toolCall.id,
    toolName: toolCall.function.name,
    argsText,
    args: parseToolArgs(argsText),
  };
  if (!response) {
    return {...base, status: 'executing'};
  }

  const isError = response.is_error ?? false;
  return {
    ...base,
    status: isError ? 'error' : 'done',
    result: toolCallResponseContent(response),
    isError,
  };
}

function parseToolArgs(
  argsText: string
): Record<string, ReadonlyJSONValue> | null {
  try {
    return JSON.parse(argsText);
  } catch {
    return null;
  }
}

/**
 * extractTurnId extracts the turn ID from a composed messageId. MessageIds
 * have the form `${turnId}:<suffix>` (e.g. `${turnId}:assistant`,
 * `${turnId}:user`) — the turn ID is everything before the first `:`. Turn
 * IDs themselves don't contain `:`.
 */
export function extractTurnId(messageId: string): string | null {
  const idx = messageId.indexOf(':');
  return idx > 0 ? messageId.slice(0, idx) : null;
}

/**
 * userMessageId composes the user message node id for a turn. Inverse of
 * {@link extractTurnId} for the user suffix; kept here so the `:user` id
 * convention lives in one place (see {@link userMessageEventForTurn}).
 */
export function userMessageId(turnId: string): string {
  return `${turnId}:user`;
}

/**
 * assistantMessageId composes the assistant message node id for a turn. Inverse
 * of {@link extractTurnId} for the assistant suffix; kept here so the
 * `:assistant` id convention lives in one place.
 */
export function assistantMessageId(turnId: string): string {
  return `${turnId}:assistant`;
}

/**
 * agentQuestionsEventForTurn returns an `agent-questions` event when the turn
 * carries structured questions for the client to present, else null. Emitted
 * alongside the terminal event so the composer can offer a richer next-prompt
 * dialog. Used by both live polling and thread hydration.
 */
export function agentQuestionsEventForTurn(
  turn: TurnResponse
): RuntimeEvent | null {
  if (!turn.agent_questions?.length) {
    return null;
  }
  return {type: 'agent-questions', questions: turn.agent_questions};
}

/**
 * networkAccessRequestForTurn returns a `network-access-request` event when the
 * turn asked for elevated sandbox network access, else null. Emitted alongside
 * the terminal event so the composer can present a grant/deny element. Used by
 * both live polling and thread hydration.
 */
export function networkAccessRequestForTurn(
  turn: TurnResponse
): RuntimeEvent | null {
  const request = turn.permission_requests?.find(
    (r): r is NetworkAccessRequest => r.type === 'network_access'
  );
  if (!request) {
    return null;
  }
  return {type: 'network-access-request', request};
}

/**
 * networkPermissionsEventForTurn reports the resolved network allowlist the turn
 * ran with, so the composer can show/revoke active grants. Emitted for every
 * turn (the latest wins); domains default to empty when none are granted.
 */
export function networkPermissionsEventForTurn(
  turn: TurnResponse
): RuntimeEvent {
  return {
    type: 'network-permissions',
    domains: turn.permissions?.allowed_network_domains ?? [],
  };
}

export function terminalMetadataEventsForTurn(
  turn: TurnResponse
): RuntimeEvent[] {
  const events: RuntimeEvent[] = [];
  const questionsEvent = agentQuestionsEventForTurn(turn);
  if (questionsEvent) {
    events.push(questionsEvent);
  }

  const networkRequestEvent = networkAccessRequestForTurn(turn);
  if (networkRequestEvent) {
    events.push(networkRequestEvent);
  }

  // Re-emitting network permissions is idempotent (the reducer overwrites
  // `activeNetworkDomains`), so callers that already emitted them mid-turn
  // don't need to opt out here.
  events.push(networkPermissionsEventForTurn(turn));

  return events;
}

export function terminalEventsForTurn(
  turn: TurnResponse,
  runId: string
): RuntimeEvent[] {
  const terminalEvent = terminalEventForTurnState(
    turn.state,
    runId,
    turn.error_info
  );
  if (!terminalEvent) {
    return [];
  }
  return [...terminalMetadataEventsForTurn(turn), terminalEvent];
}

/**
 * terminalEventForTurnState maps a backend `TurnState` to the corresponding
 * runtime terminal event. Returns null for non-terminal states (`in_progress`,
 * `queued`).
 */
export function terminalEventForTurnState(
  state: TurnState,
  runId: string,
  errorMessage?: string | null
): RuntimeEvent | null {
  switch (state) {
    case 'errored':
      return {
        type: 'run-error',
        runId,
        message:
          errorMessage || 'The agent encountered an error while processing.',
      };
    case 'cancelled':
      return {type: 'run-cancelled', runId};
    case 'completed':
      return {type: 'run-finished', runId};
    default:
      return null;
  }
}

/**
 * userMessageEventForTurn builds a turn's `user-message` event from its
 * canonical `user_prompt`, or null when the prompt has no renderable content.
 * The prompt reconstructs from `user_prompt` alone, so a turn that terminated
 * before the backend persisted its messages still renders the user's message.
 */
export function userMessageEventForTurn(
  turn: TurnResponse
): RuntimeEvent | null {
  const content = httpPromptToStoreContent(turn.user_prompt);
  if (content.length === 0) {
    return null;
  }
  return {type: 'user-message', messageId: userMessageId(turn.id), content};
}

function makeContentEvents<P extends 'text-message' | 'reasoning'>(
  prefix: P,
  messageId: string,
  content: string
): RuntimeEvent[] {
  if (prefix === 'text-message') {
    return [
      {type: 'text-message-start', messageId},
      {type: 'text-message-content', messageId, delta: content},
      {type: 'text-message-end', messageId},
    ];
  }

  return [
    {type: 'reasoning-start', messageId},
    {type: 'reasoning-content', messageId, delta: content},
    {type: 'reasoning-end', messageId},
  ];
}

function makeToolCallEvents(
  toolCall: ToolCall,
  messageId: string
): RuntimeEvent[] {
  return [
    {
      type: 'tool-call-start',
      toolCallId: toolCall.id,
      toolName: toolCall.function.name,
      messageId,
    },
    {
      type: 'tool-call-args',
      delta: toolCall.function.arguments,
      toolCallId: toolCall.id,
    },
    {type: 'tool-call-end', toolCallId: toolCall.id},
  ];
}

function makeToolCallResultEvent(
  response: ToolCallResponseRecord
): RuntimeEvent {
  return {
    type: 'tool-call-result',
    toolCallId: response.call_id,
    content: toolCallResponseContent(response),
    isError: response.is_error ?? false,
  };
}

function toolCallResponseContent(response: ToolCallResponseRecord): string {
  const output =
    typeof response.output === 'string'
      ? response.output
      : response.output == null
        ? ''
        : JSON.stringify(response.output);
  return output;
}

function httpPromptToStoreContent(prompt: HttpUserPrompt): ContentPart[] {
  if (typeof prompt === 'string') {
    return [{type: 'text', text: prompt, status: 'complete'}];
  }
  return prompt.map(promptPartToStoreContent);
}

export function promptPartToStoreContent(part: AnyPromptPart): ContentPart {
  if (!isKnownPromptPart(part)) {
    return {
      type: 'unknown',
      rawType: part.type,
      data: part as unknown as ReadonlyJSONValue,
    };
  }
  switch (part.type) {
    case 'text':
      return {type: 'text', text: part.text, status: 'complete'};
    case 'image':
      return {
        type: 'image',
        mediaType: part.media_type,
        base64Data: part.base64_data,
      };
    case 'wandb_entity':
      return {
        type: 'wandb-entity',
        entityName: part.entity_name,
      };
    case 'wandb_project':
      return {
        type: 'wandb-project',
        entityName: part.entity_name,
        projectName: part.project_name,
      };
    case 'wandb_run':
      return {
        type: 'wandb-run',
        entityName: part.entity_name,
        projectName: part.project_name,
        runName: part.run_name,
        runDisplayNameStatus: 'pending',
      };
    case 'wandb_call':
      return {
        type: 'wandb-call',
        entityName: part.entity_name,
        projectName: part.project_name,
        callId: part.call_id,
      };
    case 'wandb_conversation':
      return {
        type: 'wandb-conversation',
        entityName: part.entity_name,
        projectName: part.project_name,
        conversationId: part.conversation_id,
      };
    case 'wandb_workspace_panel':
      return {
        type: 'wandb-workspace-panel',
        entityName: part.entity_name,
        projectName: part.project_name,
        panelId: part.panel_id,
        panelType: part.panel_type,
        title: part.title ?? undefined,
        queryPanelTableKey: part.query_panel_table_key ?? undefined,
        queryPanelTableKeyType: part.query_panel_table_key_type ?? undefined,
      };
    case 'wandb_web_page_state':
      return {
        type: 'wandb-web-page-state',
        url: part.url,
        entityName: part.entity_name ?? undefined,
        projectName: part.project_name ?? undefined,
        content: part.content
          ?.map(wirePageContentToStore)
          .filter((c): c is PagePromptContent => c != null),
      };
    case 'wandb_client':
      // The client marker (platform) is per-turn metadata, not renderable
      // chat content, so it has no store part of its own — keep it as an
      // `unknown` part so it round-trips without producing a message bubble.
      return {
        type: 'unknown',
        rawType: part.type,
        data: part as unknown as ReadonlyJSONValue,
      };
  }
}

// wirePageContentToStore converts a wire page-content variant to its store
// shape, skipping any variant this build does not model so a newer backend
// cannot break turn deserialization.
function wirePageContentToStore(
  content: WirePageContent
): PagePromptContent | null {
  switch (content.type) {
    case 'workspace':
      return {
        type: 'workspace',
        viewId: content.view_id ?? undefined,
        viewName: content.view_name ?? undefined,
        viewType: content.view_type ?? undefined,
        viewScope: content.view_scope ?? undefined,
        runCountTotal: content.run_count_total ?? undefined,
        runCountFiltered: content.run_count_filtered ?? undefined,
        runCountListed: content.run_count_listed ?? undefined,
        numRunsVisible: content.num_runs_visible ?? undefined,
        numRunsHidden: content.num_runs_hidden ?? undefined,
        listOnlyVisibleRuns: content.list_only_visible_runs ?? undefined,
        runFiltersExpression: content.run_filters_expression ?? undefined,
        panelSearchQuery: content.panel_search_query ?? undefined,
        runSelectorGrouping:
          content.run_selector_grouping?.map(key => ({
            section: key.section,
            name: key.name,
          })) ?? undefined,
      };
    case 'report':
      return {
        type: 'report',
        title: content.title ?? undefined,
        description: content.description ?? undefined,
        runSets: content.run_sets?.map(runSet => ({
          name: runSet.name ?? undefined,
          entityName: runSet.entity_name ?? undefined,
          projectName: runSet.project_name ?? undefined,
          runCountFiltered: runSet.run_count_filtered ?? undefined,
          runFiltersExpression: runSet.run_filters_expression ?? undefined,
          enabled: runSet.enabled ?? undefined,
        })),
      };
    case 'sweep':
      return {
        type: 'sweep',
        state: content.state ?? undefined,
        method: content.method ?? undefined,
        metric: content.metric
          ? {
              name: content.metric.name,
              goal: content.metric.goal ?? undefined,
            }
          : undefined,
        runCount: content.run_count ?? undefined,
        runCountExpected: content.run_count_expected ?? undefined,
      };
    default:
      return skipUnknownPageContent(content);
  }
}

function skipUnknownPageContent(_variant: never): null {
  return null;
}
